import { Info, Note } from "@phosphor-icons/react"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Card } from "@tokenterminal/core/ui/layout/card/Card"
import { useMemo, useEffect, useState } from "react"
import { twMerge } from "tailwind-merge"
import { getBackgroundStyles } from "./themes"
import {
  cardPaddingPixelsForPadding,
  dimensionsForAspectRatio,
  nonCardPaddingPixelsForPadding,
  paddingPixelsForPadding,
  type ChartImageState,
} from "./types"

// Responsible for rendering a card with a chart-like UI inside. Includes background image, padding, and appropriate dimensions.

export const ChartImagePreviewWrapper = ({
  innerRef,
  state,
  children,
  sourcesAndDefinitionsText,
  footnoteText,
  lazyRender = false,
  showSettings,
}: {
  children: React.ReactNode
  innerRef: React.RefObject<HTMLDivElement>
  state: ChartImageState
  sourcesAndDefinitionsText?: string
  footnoteText?: string
  dimensionsStyles?: Record<string, number>
  lazyRender?: boolean
  showSettings?: boolean
}) => {
  const [mounted, setMounted] = useState(false)
  const { background, text } = getBackgroundStyles(state.background)

  // Charts internally have a resize observer that updates the chart size when
  // the window is resized. When the modal is opened, the chart can get stuck in
  // a state where it's not the correct size, I think due to the modal's initial
  // animation. This is a hacky fix to force the chart to resize once the modal
  // is done with it's initial animation.
  useEffect(() => {
    setTimeout(() => {
      setMounted(true)
    }, 200)
  }, [])
  const lazyChildren = useMemo(() => {
    if (!lazyRender) {
      return children
    }

    return mounted ? (
      <div
        className="animate-fade-in flex flex-col flex-1 h-full"
        style={{
          animationDuration: "300ms",
        }}
      >
        {children}
      </div>
    ) : null
  }, [children, mounted, lazyRender])

  const padding = paddingPixelsForPadding[state.padding]

  const imageDimensions = useMemo(() => {
    const base = dimensionsForAspectRatio[state.aspectRatio]
    return {
      width: base.width,
      height: base.height,
    }
  }, [state.aspectRatio])

  const footerContent = useMemo(() => {
    return (
      <div
        className="flex flex-col gap-3"
        style={{
          marginTop: `${cardPaddingPixelsForPadding[state.padding] / 2}px`,
        }}
      >
        {state.showSources && sourcesAndDefinitionsText && (
          <div className="flex gap-3">
            <Icon as={Info} width={12} className="mt-[2px] flex-shrink-0" />
            <div className="text-xs break-words w-full opacity-70">
              {sourcesAndDefinitionsText}
            </div>
          </div>
        )}
        {state.showFootnote && footnoteText && (
          <div className="flex gap-3">
            <Icon as={Note} width={12} className="mt-[2px] flex-shrink-0" />
            <div className="text-xs break-words w-full opacity-70">
              {footnoteText}
            </div>
          </div>
        )}
      </div>
    )
  }, [
    state.showSources,
    state.padding,
    sourcesAndDefinitionsText,
    state.showFootnote,
    footnoteText,
  ])

  return (
    <div
      className={twMerge(
        "shadow shadow-black/10 dark:shadow-black/20 overflow-hidden rounded-md border border-solid border-border-default",
        showSettings
          ? "rounded-md border border-solid border-border-default"
          : ""
      )}
    >
      <div
        ref={innerRef}
        className={twMerge(
          "flex flex-col",
          state.showBackground ? background.className : "",
          "flex items-center justify-center"
        )}
        style={{
          padding: state.showBackground ? `${padding}px` : "0px",
          ...background.style,
        }}
      >
        {/* Dimensions container. If we're not showing a background, we want to render a plain div with padding */}
        <div
          className={twMerge(
            "h-full flex flex-col",
            !state.showBackground && "bg-bg-surface"
          )}
          style={{
            width: imageDimensions.width,
            height: imageDimensions.height,
            padding: state.showBackground
              ? 0
              : `${nonCardPaddingPixelsForPadding[state.padding]}px`,
          }}
        >
          {/* If we're showing a background, we want to render a card with the children inside */}
          <div
            className={twMerge(
              "flex flex-col gap-4 h-full",
              state.showBackground && `${Card.className} rounded-xl`
            )}
            style={{
              padding: state.showBackground
                ? `${cardPaddingPixelsForPadding[state.padding]}px`
                : 0,
              // border: "none",
              // boxShadow: "0 0 0 1px hsl(var(--fg-default) / 5%)",
            }}
          >
            {/* Since the lazy children pop in, this helps avoid a layout shift */}
            <div className="flex-1">{lazyChildren}</div>
            {state.sourcesAndFootnoteDisplay === "inside-card" &&
              (state.showSources || state.showFootnote) && (
                <div className="text-fg-secondary">{footerContent}</div>
              )}
          </div>
          {state.sourcesAndFootnoteDisplay === "out-of-card" &&
            (state.showSources || state.showFootnote) && (
              <div
                className={text.className}
                style={{
                  ...text.style,
                }}
              >
                {footerContent}
              </div>
            )}
        </div>
      </div>
    </div>
  )
}
